/**
 * ゲーム結果のシェア画像を Canvas API で生成するユーティリティ
 */

export interface ShareImageOptions {
  /** ゲーム名 */
  toolName: string;
  /** メインスコア表示（大きい数字部分） */
  mainValue: string;
  /** スコアの単位・ラベル */
  mainLabel: string;
  /** 補足テキスト（コンボ、スピン回数等） */
  subText?: string;
  /** バッジテキスト（新記録、クリア等） */
  badge?: string;
  /** テーマカラー [メイン, ダーク] */
  themeColors?: [string, string];
}

const W = 1200;
const H = 630;
const FONT =
  '"Hiragino Sans", "Hiragino Kaku Gothic ProN", "Noto Sans JP", "Yu Gothic", sans-serif';

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export async function generateShareImage(
  options: ShareImageOptions,
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  const [c1, c2] = options.themeColors ?? ['#10b981', '#065f46'];

  // ── Background gradient ──
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, c1);
  bg.addColorStop(1, c2);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // ── Decorative circles (subtle) ──
  ctx.globalAlpha = 0.08;
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.arc(150, 130, 200, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(W - 180, H - 100, 160, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;

  // ── White card ──
  const cardX = 48;
  const cardY = 48;
  const cardW = W - 96;
  const cardH = H - 96;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.97)';
  ctx.beginPath();
  ctx.roundRect(cardX, cardY, cardW, cardH, 28);
  ctx.fill();

  // ── Tool name ──
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = c1;
  ctx.font = `bold 42px ${FONT}`;
  ctx.fillText(options.toolName, W / 2, 130);

  // ── Decorative line ──
  const titleW = ctx.measureText(options.toolName).width;
  ctx.strokeStyle = c1;
  ctx.globalAlpha = 0.3;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(W / 2 - titleW / 2 - 20, 158);
  ctx.lineTo(W / 2 + titleW / 2 + 20, 158);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // ── Main score ──
  ctx.fillStyle = '#111827';
  ctx.font = `bold 120px ${FONT}`;
  // Shrink font if text is too wide
  let fontSize = 120;
  while (ctx.measureText(options.mainValue).width > cardW - 120 && fontSize > 48) {
    fontSize -= 4;
    ctx.font = `bold ${fontSize}px ${FONT}`;
  }
  ctx.fillText(options.mainValue, W / 2, 255);

  // ── Main label ──
  ctx.fillStyle = '#6b7280';
  ctx.font = `34px ${FONT}`;
  ctx.fillText(options.mainLabel, W / 2, 355);

  // ── Badge ──
  let nextY = 410;
  if (options.badge) {
    ctx.fillStyle = '#d97706';
    ctx.font = `bold 34px ${FONT}`;
    ctx.fillText(options.badge, W / 2, nextY);
    nextY += 48;
  }

  // ── Sub text ──
  if (options.subText) {
    ctx.fillStyle = '#9ca3af';
    ctx.font = `28px ${FONT}`;
    ctx.fillText(options.subText, W / 2, nextY);
  }

  // ── Character image ──
  try {
    const img = await loadImage('/images/icon_koke.png');
    const size = 64;
    ctx.globalAlpha = 0.5;
    ctx.drawImage(img, W / 2 - size / 2, H - 155, size, size);
    ctx.globalAlpha = 1;
  } catch {
    // skip if image fails
  }

  // ── Branding ──
  ctx.fillStyle = '#9ca3af';
  ctx.font = `22px ${FONT}`;
  ctx.fillText('企画苔 - 便利ツール - ミニゲーム', W / 2, H - 78);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Failed to create image'))),
      'image/png',
    );
  });
}
